import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { FormItemConfigurationModel } from '../../../../models/forms/form-item-configuration.model';
import { FormItemDataModel } from '../../../../models/forms/data/form-item-data.model';
import { PatientCaseWorkItemFormDataModel } from '../../../../models/forms/data/patient-case-work-item-form-data.model';
import { FormParagraphDataModel } from '../../../../models/forms/data/form-paragraph-data.model';
import { FormParagraphConfigurationModel } from '../../../../models/forms/form-paragraph-configuration.model';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';
import { StaticDataService } from '../../../../services/static-data.service';

@Component({
  standalone: false,
  selector: 'app-forms-builder-polished-preview',
  templateUrl: './forms-builder-polished-preview.component.html',
  styleUrls: ['./forms-builder-polished-preview.component.css']
})
export class FormsBuilderPolishedPreviewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('previewViewport') previewViewportRef: ElementRef<HTMLElement>;

  // Embedded usage: the AI Studio renders this preview inline (not as a modal) and re-feeds a
  // fresh form-version reference after each AI turn. When set, `formVersion` is the render source
  // instead of MAT_DIALOG_DATA. `embedded` relaxes the full-window modal chrome.
  @Input() formVersion: FormVersionConfigurationModel;
  @Input() embedded: boolean = false;

  // The paragraph builder hosts its own copies of the view controls in its top bar, so it suppresses
  // this component's control strip and drives the two view flags from above instead. The full-screen
  // dialog and the AI Studio leave `hideChrome` off and keep using the built-in controls.
  @Input() hideChrome: boolean = false;
  @Input() mobilePreview: boolean = false;
  @Input() useDocumentFrame: boolean = true;

  cfgFormVersion: FormVersionConfigurationModel;
  dataForm: PatientCaseWorkItemFormDataModel;
  isLoading: boolean = false;
  refreshingView: boolean = false;
  documentFormat: 'letter' | 'a4' = 'letter';

  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  // Incremented per renderVersion() call so a superseded render can discard its own result.
  private renderToken: number = 0;

  constructor(
    @Optional() public dialogRef: MatDialogRef<FormsBuilderPolishedPreviewComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
    private staticDataService: StaticDataService,
  ) { }

  ngOnInit(): void {
    this.renderVersion(this.data?.cfgFormVersion ?? this.formVersion);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-render when the embedding parent swaps in a new working version (skip the initial
    // binding — ngOnInit handles first render).
    if (changes['formVersion'] && !changes['formVersion'].firstChange) {
      this.renderVersion(this.formVersion);
    }

    // View flags driven from a parent's toolbar need the same follow-up work the built-in controls do,
    // otherwise the frame keeps the dimensions it had before the switch.
    if (changes['useDocumentFrame'] && !changes['useDocumentFrame'].firstChange) {
      this.scheduleDocumentFormatUpdate(0);
    }

    if (changes['mobilePreview'] && !changes['mobilePreview'].firstChange) {
      this.resetView();
    }
  }

  // Deep-clones the source version, strips soft-deleted objects, and rebuilds the spoofed data
  // form so <app-forms-form> can render it read-only. Shared by modal init and embedded updates.
  private renderVersion(source: FormVersionConfigurationModel): void {
    if (!source) {
      return;
    }
    const token = ++this.renderToken;
    this.cfgFormVersion = JSON.parse(JSON.stringify(source));
    this.filterOutAllDeletedObjects();
    this.scheduleDocumentFormatUpdate();
    this.populateSpoofData().then((dataForm: any) => {
      // Drop the result if a newer render has started. Spoof data is built entirely synchronously
      // today (populateItem resolves on the same tick), so these cannot currently resolve out of
      // order — this keeps that from turning into a stale preview if any population step ever grows
      // a real async dependency, e.g. fetching valid values instead of reading them from cache.
      if (token !== this.renderToken) {
        return;
      }
      this.dataForm = dataForm;
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.scheduleDocumentFormatUpdate();
  }

  ngOnDestroy(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
  }

  filterOutAllDeletedObjects(): void {
    this.cfgFormVersion.cfgDFormParagraphs = this.cfgFormVersion.cfgDFormParagraphs.filter(paragraph => {
      return paragraph.xauditDeleted == false;
    });

    this.cfgFormVersion.cfgDFormParagraphs.forEach(paragraph => {
      paragraph.cfgDFormItems = paragraph.cfgDFormItems.filter(item => {
        return item.xauditDeleted == false;
      });
    });
  }

  close(): void {
    this.dialogRef?.close();
  }

  populateSpoofData() {
    let promise = new Promise((resolve, reject) => {
      let dataForm = new PatientCaseWorkItemFormDataModel();

      dataForm.DFormId = this.cfgFormVersion.DFormId;
      dataForm.formOrder = 0;
      dataForm.CaseWorkItemFormsId = '';
      dataForm.dataFormParagraphs = [];
      let promises = [];
      this.cfgFormVersion.cfgDFormParagraphs.map((paragraph, i) => {
        promises.push(this.populateParagraph(paragraph, i));
      });
      Promise.all(promises).then((results: FormParagraphDataModel[]) => {
        dataForm.dataFormParagraphs = results;
        resolve(dataForm);
      });
    });
    return promise;
  }

  populateParagraph(paragraph: FormParagraphConfigurationModel, index: number) {
    let promise = new Promise((resolve, reject) => {
      let dataParagraph = new FormParagraphDataModel();
      dataParagraph.dataFormParagraphID = paragraph.DFormParagraphId || `preview-paragraph-${index}`;
      dataParagraph.CaseWorkItemFormsId = '';
      dataParagraph.DFormParagraphId = paragraph.DFormParagraphId;
      dataParagraph.ParagraphOrder = paragraph.ParagraphOrder;
      dataParagraph.cfgDFormParagraph = JSON.parse(JSON.stringify(paragraph));
      dataParagraph.dataFormItems = [];
      let promises = [];
      paragraph.cfgDFormItems.forEach((item, i) => {
        promises.push(this.populateItem(item, JSON.parse(JSON.stringify(dataParagraph)), i));
      });

      Promise.all(promises).then((results: FormItemDataModel[]) => {
        dataParagraph.dataFormItems = results;
        resolve(dataParagraph);
      });
    });
    return promise;
  }

  populateItem(item: FormItemConfigurationModel, paragraph: FormParagraphDataModel, index: number) {
    let promise = new Promise((resolve, reject) => {
      let dataItem = new FormItemDataModel();
      dataItem.dataFormItemId = item.DFormItemId || `${paragraph.dataFormParagraphID}-preview-item-${index}`;
      dataItem.dataFormParagraphID = paragraph.dataFormParagraphID;
      dataItem.CaseWorkItemFormsId = '';
      dataItem.ItemName = item.DFormItemName;
      dataItem.DFormItemId = item.DFormItemId;
      dataItem.HasChildren = item.HasChildren;
      dataItem.ParentdataFormItemId = item.ParentDFormItemId;
      dataItem.cfgDFormItem = JSON.parse(JSON.stringify(item));
      dataItem.dataFormResults = [];
      // Must be empty array - checkbox/radio list controls call .find()/.forEach() on RESULTS.
      dataItem.RESULTS = [];

      if (dataItem.cfgDFormItem.ValidValueId) {
        const valueId = dataItem.cfgDFormItem.ValidValueId.toLowerCase();
        const validValue = this.staticDataService.validValues.find(p => p.ValidValueId.toLowerCase() === valueId);
        if (validValue) {
          dataItem.cfgDFormItem.ValidValue = { ...validValue };
          const validItems = this.staticDataService.validValueItems
            .filter(p => p.ValidValueId?.toLowerCase() === valueId)
            .sort((a, b) => a.ItemOrder - b.ItemOrder);
          dataItem.cfgDFormItem.ValidValue.enumValidValueItems = validItems;
        }
      }

      resolve(dataItem);
    });
    return promise;
  }

  resetView(): void {
    this.refreshingView = true;
    this.cdr.detectChanges();
    this.scheduleDocumentFormatUpdate(100);
    setTimeout(() => {
      this.refreshingView = false;
      this.cdr.detectChanges();
      this.scheduleDocumentFormatUpdate(50);
    }, 500);
  }

  togglePreviewLayout(): void {
    this.useDocumentFrame = !this.useDocumentFrame;
    this.scheduleDocumentFormatUpdate(0);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleDocumentFormatUpdate(120);
  }

  private scheduleDocumentFormatUpdate(delay: number = 0): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }

    this.resizeTimer = setTimeout(() => {
      this.updateDocumentFormatByViewport();
    }, delay);
  }

  private updateDocumentFormatByViewport(): void {
    if (this.mobilePreview || !this.useDocumentFrame) {
      return;
    }

    const viewport = this.previewViewportRef?.nativeElement;
    if (!viewport) {
      return;
    }

    const availableWidth = Math.max(0, viewport.clientWidth - 32);
    const availableHeight = Math.max(0, viewport.clientHeight - 32);
    if (availableWidth <= 0 || availableHeight <= 0) {
      return;
    }

    const letterRatio = 8.5 / 11;
    const a4Ratio = 210 / 297;
    const letterArea = this.getFitArea(availableWidth, availableHeight, letterRatio);
    const a4Area = this.getFitArea(availableWidth, availableHeight, a4Ratio);
    this.documentFormat = a4Area > letterArea ? 'a4' : 'letter';
  }

  private getFitArea(availableWidth: number, availableHeight: number, ratio: number): number {
    const fitWidth = Math.min(availableWidth, availableHeight * ratio);
    const fitHeight = fitWidth / ratio;
    return fitWidth * fitHeight;
  }
}

